import { AppTextService } from '@fi-shared/app-text';
import { FiDialogsService } from '@fi-shared/fi-dialogs/fi-dialogs.service';
import { Component, OnDestroy, OnInit, Renderer2, ViewEncapsulation } from '@angular/core';
import { DataFieldModel } from '../../data-field.model';
import { MatSlideToggleChange } from '@angular/material/slide-toggle';
import { fiTryContentLanguageValue } from '@fi-shared/utils/try-content-language-value';
import { DataFieldType } from '../../data-field-type';
import { DataFieldPropertyType } from '../../data-field-property-type';
import { DataFieldVisibleForType } from '../../data-field-visible-for-type';
import { DataFieldGroupEditItemDialogConfirmTypeConvertComponent } from '../dialog-confirm-type-convert/dialog-confirm-type-convert.component';
import { DataFieldPreSetOptionModel } from '../../data-field-pre-set-option.model';
import { hideAllMatDialogs } from '@fi-app/utilities/hide-all-mat-dialogs';
import { FiDialogsContentBase } from '@fi-shared/fi-dialogs/fi-dialogs-content-base';
import { FiDialogCloseReason } from '@fi-shared/fi-dialogs/fi-dialogs-close-reason';
import { FiValuesComparison } from '@fi-shared/utils/fi-values-comparison';
import { Subject } from 'rxjs';
import {
    FiToggleSwitchLabelAlign,
    FiToggleSwitchSliderColorClass,
} from '@fi-shared/fi-toggle-switch/fi-toggle-switch.component';

interface IDialogEditItemData {
    item: DataFieldModel;
    standardField?: boolean;
    languageId: number;
    selectedLanguage;
}

export interface IDialogEditItemResult {
    Name?: string;
    IsEnabled: boolean;
    IsSetRequired: boolean;
    CanSwitchRequired: boolean;
    Type?: DataFieldType;
    VisibleFor: DataFieldVisibleForType;
    IsMultipleChoice: boolean;
    PreSetOptions: Array<DataFieldPreSetOptionModel>;
}

@Component({
    selector: 'fi-data-field-group-edit-dialog-edit-item',
    templateUrl: './dialog-edit-item.component.html',
    styleUrls: ['./dialog-edit-item.component.scss'],
    encapsulation: ViewEncapsulation.None,
    standalone: false,
})
export class DataFieldGroupEditItemDialogComponent
    extends FiDialogsContentBase<IDialogEditItemData, IDialogEditItemResult>
    implements OnInit, OnDestroy
{
    resultModel: IDialogEditItemResult;
    fieldTypeArray = [
        {
            Id: DataFieldType.Text,
            TextId: 'data_field_edit_dialog.type_list_text',
        },
        {
            Id: DataFieldType.Numeric,
            TextId: 'data_field_edit_dialog.type_list_numeric',
        },
        {
            Id: DataFieldType.Calendar,
            TextId: 'data_field_edit_dialog.type_list_calendar',
        },
    ];

    visibleForTypeArray = [
        {
            Id: DataFieldVisibleForType.AdminOnly,
            TextId: 'data_field_edit_dialog.visible_admin',
        },
        {
            Id: DataFieldVisibleForType.Everyone,
            TextId: 'data_field_edit_dialog.visible_everyone',
        },
        {
            Id: DataFieldVisibleForType.LocalManagersOrHigher,
            TextId: 'data_field_edit_dialog.visible_local_manager',
        },
        {
            Id: DataFieldVisibleForType.CentralManagersOrHigher,
            TextId: 'data_field_edit_dialog.visible_central_manager',
        },
        {
            Id: DataFieldVisibleForType.GlobalManagersOrHigher,
            TextId: 'data_field_edit_dialog.visible_global_manager',
        },
    ];

    private fiDialogsService: FiDialogsService;
    private appTextService: AppTextService;
    private renderer: Renderer2;
    fiValuesComparison: FiValuesComparison<IDialogEditItemResult>;

    ngOnInit() {
        if (!this.data.selectedLanguage) throw new Error('FI: IDialogEditItemData.selectedLanguage is required');

        this.fiDialogsService = this.injector.get(FiDialogsService);
        this.appTextService = this.injector.get(AppTextService);
        this.renderer = this.injector.get(Renderer2);

        this.resultModel = {
            Name: fiTryContentLanguageValue(this.data.item.Names, this.data.languageId),
            IsEnabled: this.data.item.IsEnabled,
            IsSetRequired: this.data.item.IsSetRequired,
            CanSwitchRequired: this.data.item.CanSwitchRequired,
            Type: this.data.item.Type || DataFieldType.Text,
            VisibleFor: this.data.item.VisibleFor || DataFieldVisibleForType.AdminOnly,
            IsMultipleChoice: this.data.item.IsMultipleChoice || false,
            PreSetOptions: this.data.item.PreSetOptions ? copyPresetOptions(this.data.item.PreSetOptions) : [],
        };
        this.fiValuesComparison = new FiValuesComparison(() => this.resultModel);
        this.subscribeOnTransitionCloseDialog(this.fiDialogsService, this.ngOnDestroy$);

        this._checkAddPreSetOption();
    }

    isYesButtonDisabled() {
        return !this.resultModel.Name;
    }

    onYesButtonClick($event) {
        $event.preventDefault();
        this._doneClickCheckAskTypeConvert().then(() =>
            this.dialogRef.close({
                value: this.resultModel,
                reason: FiDialogCloseReason.YesButton,
            }),
        );
    }

    _doneClickCheckAskTypeConvert(): Promise<void> {
        const askTextId = this._getCheckAskTypeConvertTextId();
        if (!askTextId) {
            return Promise.resolve();
        }

        return new Promise((resolve) =>
            hideAllMatDialogs(() => {
                const modalRef = this.fiDialogsService
                    .custom({
                        contentComponentRef: DataFieldGroupEditItemDialogConfirmTypeConvertComponent,
                        contentComponentData: {
                            textId: askTextId,
                        },
                        config: {
                            panelClass: 'data-field-group-confirm-type-change',
                        },
                    })
                    .onYesButtonClick(() => {
                        resolve();
                    });

                return modalRef.dialogRef;
            }, this.renderer),
        );
    }

    _getCheckAskTypeConvertTextId() {
        if (!this.data.item.Id) return;

        if (this.data.item.Type === DataFieldType.Text && this.resultModel.Type === DataFieldType.Numeric)
            return 'data_field_confirm_type_change.text';

        if (
            (this.data.item.Type === DataFieldType.Text || this.data.item.Type === DataFieldType.Numeric) &&
            this.resultModel.Type === DataFieldType.Calendar
        )
            return 'data_field_confirm_type_change.text_calendar';
    }

    isMultipleChoiceClick() {
        if (this.resultModel.IsMultipleChoice) {
            this.fiDialogsService
                .custom({
                    headerText: this.appTextService.get('dialog.header_confirm'),
                    yesButtonText: this.appTextService.get('done').toUpperCase(),
                    noButtonText: this.appTextService.get('common.cancel').toUpperCase(),
                    contentComponentRef: DataFieldGroupEditItemDialogConfirmTypeConvertComponent,
                    contentComponentData: {
                        textId: 'data_field_confirm_multiple_choice_change.text',
                    },
                    config: {
                        panelClass: 'data-field-group-confirm-type-change',
                    },
                })
                .onYesButtonClick((result) => {
                    if (result) this.resultModel.IsMultipleChoice = false;
                });
        }
    }
    //
    // isMultipleChoiceChange($event: MatSlideToggleChange) {
    //     this.resultModel.IsMultipleChoice = $event.checked;
    // }
    //
    // enabledChange($event: MatSlideToggleChange) {
    //     this.resultModel.IsEnabled = $event.checked;
    // }

    setRequiredChange() {
        if (this.resultModel.IsSetRequired) {
            this.resultModel.IsEnabled = true;
        }
    }

    isCustomField() {
        return this.data.item.PropertyType === DataFieldPropertyType.Custom;
    }

    isPreSetOptions() {
        return this.isCustomField() && this.resultModel.Type === DataFieldType.PreSetOptions;
    }

    _checkAddPreSetOption() {
        if (this.data.item.Id && this.data.item.Type !== DataFieldType.PreSetOptions) return false;

        this.fieldTypeArray.push({
            Id: DataFieldType.PreSetOptions,
            TextId: 'data_field_edit_dialog.type_list_pre_set_options',
        });
    }

    isFieldTypeDisabled() {
        return this.isCustomField() && this.data.item.Id && this.data.item.Type === DataFieldType.PreSetOptions;
    }

    getResult(): IDialogEditItemResult {
        return this.resultModel;
    }

    getNoButtonClickPromise(): Promise<{ canClose: boolean }> {
        return this.fiDialogsService.getCanCloseConfirmation(!this.fiValuesComparison.isChanged());
    }

    private ngOnDestroy$ = new Subject<void>();
    ngOnDestroy() {
        this.ngOnDestroy$.next();
        this.ngOnDestroy$.complete();
    }

    protected readonly FiToggleSwitchSliderColorClass = FiToggleSwitchSliderColorClass;
    protected readonly FiToggleSwitchLabelAlign = FiToggleSwitchLabelAlign;
}

function copyPresetOptions(options: Array<DataFieldPreSetOptionModel>): Array<DataFieldPreSetOptionModel> {
    return options.map((model) => new DataFieldPreSetOptionModel(model));
}
